/**
 * Reloj del entreno basado en marcas de tiempo (WorkoutClock.swift): el tiempo transcurrido se calcula siempre con
 * fechas reales, nunca restando segundos, así que el techo salta bien aunque la app se suspenda. La pausa también.
 */
export interface ClockState {
  startedAt: number;        // ms epoch
  ceilingSeconds: number;
  accumulatedPauseMs: number;
  pausedAt: number | null;  // ms epoch
}

export const newClock = (startedAt: number, ceilingSeconds: number): ClockState =>
  ({ startedAt, ceilingSeconds, accumulatedPauseMs: 0, pausedAt: null });

export const isPaused = (c: ClockState) => c.pausedAt !== null;

/** Segundos de entreno activo (sin pausas) en `now`. */
export const elapsedSeconds = (c: ClockState, now: number) =>
  Math.max(0, ((c.pausedAt ?? now) - c.startedAt - c.accumulatedPauseMs) / 1000);

export const remainingSeconds = (c: ClockState, now: number) => Math.max(0, c.ceilingSeconds - elapsedSeconds(c, now));

export const isCeilingReached = (c: ClockState, now: number) => elapsedSeconds(c, now) >= c.ceilingSeconds;

/** Duración a guardar: lo transcurrido con el techo como máximo, en segundos enteros. */
export const frozenDurationSeconds = (c: ClockState, now: number) => Math.round(Math.min(elapsedSeconds(c, now), c.ceilingSeconds));

export const pause = (c: ClockState, now: number): ClockState => (c.pausedAt === null ? { ...c, pausedAt: now } : c);

export const resume = (c: ClockState, now: number): ClockState =>
  (c.pausedAt === null ? c : { ...c, accumulatedPauseMs: c.accumulatedPauseMs + (now - c.pausedAt), pausedAt: null });

/** mm:ss */
export function clockString(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
}
